import { Plus, XCircle } from "lucide-react"
import { useMemo, useState } from "react"
import { Upload } from "../common/upload"
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Image } from "../common/image";
import { Tooltip } from "../ui/modal";
import { ChangeIcon } from "./assets/change-icon";
import { ReferenceImage } from "./ai-creation-components";
import { AiCreationType } from "@/manager/manager/agent-user-messages-manager";
import { getModelOptionByValue, imageModelOptions } from "./constants/ai-creation-model";
import { cn } from "@/lib/utils";
import { LoadingOverlay } from "../common/loading-overlay";
import { UPLOAD_ERROR_TYPE } from "@/hooks/use-upload";

export const AiCreationButton = ({
  aiCreationType,
  aiCreationModel,
  referenceImages,
  disabled,
  disabledRemove,
  frameType,
  isFontEndFrameImage,
  uploadReferenceImages,
  uploadReferenceImagesLoading,
  onReferenceImagesChange,
}: {
  aiCreationType: AiCreationType,
  aiCreationModel: string,
  referenceImages?: string[],
  uploadReferenceImagesLoading?: boolean,
  isFontEndFrameImage?: boolean,
  isEndFrameImage?: boolean,
  frameType?: 'font' | 'end' | null,
  disabled?: boolean,
  disabledRemove?: boolean,
  onReferenceImagesChange?: (images: string[]) => void
  uploadReferenceImages?: (files: File | File[]) => void,
}) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  // 是否是 NanoBanana 普通版
  const isNanoBananaRegular = aiCreationModel === imageModelOptions()[0].value;

  const handleUpload = async (file: File | File[]) => {
    uploadReferenceImages(file);
  }

  // 使用 useMemo 锁定 URL 列表
  const imageUrls = useMemo(() => {
    if (!referenceImages) return [];
    const urls = referenceImages.map((file) => {
      if (typeof file === 'string') {
        return file;
      }
      return URL.createObjectURL(file);
    });
    return urls
  }, [referenceImages, isNanoBananaRegular]);

  // 只有 NanoBanana 模型才需要上传卡片
  const isShowUploadCard = useMemo(() => {
    // 选择普通版
    if (isNanoBananaRegular) {
      return imageUrls.length < imageModelOptions()[0].maxImages
    } else {
      // 选择pro版
      return imageUrls.length < imageModelOptions()[1].maxImages
    }
  }, [aiCreationModel, imageUrls, isNanoBananaRegular])

  const modelOption = getModelOptionByValue(aiCreationModel);

  // 文件上传失败
  const handleUploadInvalid = (type: keyof typeof UPLOAD_ERROR_TYPE) => {
    let msg = '';
    switch (type) {
      case UPLOAD_ERROR_TYPE.INVALID_TYPE:
        msg = "Please upload images in the required type"
        break;
      case UPLOAD_ERROR_TYPE.INVALID_RESOLUTION:
        msg = t('ai_creation.upload.upload_error_resolution')
        break;
      case UPLOAD_ERROR_TYPE.INVALID_MAX_SIZE:
        msg = t('ai_creation.upload.upload_error_max_size', { msg: `${modelOption.maxInputFileSize / 1024 / 1024}MB` })
        break;
      default:
        msg = "Please upload the required images"
        break;
    }
    toast.default({
      description: msg,
    });
  }

  const handleRemove = (index: number) => {
    onReferenceImagesChange?.(referenceImages.filter((_, i) => i !== index));
  }

  return (
    <div className="relative min-w-[52px] max-w-[52px]">
      {!referenceImages?.length && (
        <ReferenceUploadCard
          aiCreationType={aiCreationType}
          aiCreationModel={aiCreationModel}
          onUpload={handleUpload}
          uploading={uploadReferenceImagesLoading}
          frameType={frameType}
          endFrameDisabled={disabled}
          onUploadInvalid={handleUploadInvalid}
        />
      )}
      {referenceImages?.length > 0 && (
        <div className="relative size-full">
          <ReferenceImage
            urls={imageUrls}
            showRemove={true}
            aiCreationModel={aiCreationModel}
            showUploadCard={aiCreationType === 'image' && isShowUploadCard}
            aiCreationType={aiCreationType}
            onUpload={handleUpload}
            onRemove={handleRemove}
            disabledRemove={disabledRemove}
            onUploadInvalid={handleUploadInvalid}
          />
        </div>
      )}
    </div>
  )
}

/** 参考图片上传卡片 */
export const ReferenceUploadCard = ({
  className,
  aiCreationType,
  aiCreationModel,
  frameType,
  endFrameDisabled,
  disabled,
  tooltipOffset = 35,
  uploading,
  onUpload,
  onUploadInvalid,
}: {
  aiCreationType: AiCreationType,
  aiCreationModel: string,
  tooltipOffset?: number,
  frameType?: 'font' | 'end',
  disabled?: boolean,
  endFrameDisabled?: boolean,
  className?: string,
  uploading?: boolean,
  onUpload: (file: File | File[]) => void,
  onUploadInvalid?: (type: keyof typeof UPLOAD_ERROR_TYPE) => void,
}) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleUpload = async (file: File | File[]) => {
    onUpload?.(file);
  }

  // 获取模型选项
  const modelOption = getModelOptionByValue(aiCreationModel);

  return (
    <Upload
      className={cn(
        "min-w-[52px] max-w-[52px] !min-h-[70px] rounded-[8px] bg-gray-100 cursor-pointer transition-all hover:shadow-sm z-[20] relative size-full flex items-center justify-center duration-200 hover:scale-110 active:scale-95",
        disabled || endFrameDisabled && "cursor-not-allowed opacity-60",
        className
      )}
      disabled={disabled || endFrameDisabled}
      multiple={aiCreationType === 'image'}
      acceptedFileTypes={modelOption?.acceptedInputFileTypes}
      maxFileSize={modelOption?.maxInputFileSize || 10 * 1024 * 1024}
      minFileSize={0}
      maxImageWidth={modelOption?.maxImageWidth}
      maxImageHeight={modelOption?.maxImageHeight}
      onUpload={handleUpload}
      onInvalid={(type) => onUploadInvalid?.(type)}
    >
      <Tooltip
        side='top'
        align='center'
        offset={tooltipOffset}
        wrapperClassName="size-full min-h-[70px] flex items-center justify-center"
        trigger={
          <div className="flex items-center justify-center relative size-full select-none min-w-5 min-h-5">
            {!uploading && (
              <div className="flex flex-col justify-center items-center gap-0.5">
                <Plus className="size-5 text-[rgba(152,162,179,1)]" />
                {frameType && <span className="text-[rgba(152,162,179,1)] text-xs text-center">
                  {frameType === 'font' ? t('ai_creation.upload.font_frame') : t('ai_creation.upload.end_frame')}
                </span>}
              </div>
            )}
            <LoadingOverlay loading={uploading} className="bg-gray-100" />
          </div>
        }
        content={
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
              {!frameType && t('ai_creation.upload.image_upload_tips')}
              {frameType === 'font' && t('ai_creation.upload.font_frame_upload_tips')}
              {frameType === 'end' && !endFrameDisabled && t('ai_creation.upload.end_frame_upload_tips')}
              {frameType === 'end' && endFrameDisabled && t('ai_creation.upload.please_upload_font_frame')}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
          </div>
        }
      />
    </Upload>
  )
}